import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Box, 
  Menu, 
  MenuItem, 
  Avatar, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider,
  useMediaQuery,
  Container,
  Badge
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Settings as SettingsIcon,
  Calculate as CalculateIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  EmojiEvents as EmojiEventsIcon,
  Payments as PaymentsIcon,
  Videocam as VideocamIcon,
  Message as MessageIcon,
  Quiz as QuizIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  MilitaryTech as MilitaryTechIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { getStudentNavItems, StudentNavItem } from './studentNavItems';

function Header() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { authState, logout } = useAuth();
  const { isAuthenticated, user } = authState;
  const { tenantInfo } = useThemeContext();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  // Feature flag for teams
  const teamsEnabled = process.env.REACT_APP_ENABLE_TEAMS === 'true';
  const studentNavItems = getStudentNavItems({ teamsEnabled });
  // Hint Videos sits only in the mobile drawer (see studentNavItems.ts) at
  // its original position, right after Ranked — split the shared array here
  // so the drawer can splice it back in without duplicating the rest of the
  // list.
  const studentRankedIndex = studentNavItems.findIndex(item => item.to === '/student/ranked');
  const studentNavItemsBeforeHintVideos = studentNavItems.slice(0, studentRankedIndex + 1);
  const studentNavItemsAfterHintVideos = studentNavItems.slice(studentRankedIndex + 1);

  // Check if a path is active - used for highlighting current navigation item
  const isActive = (path: string): boolean => {
    // Special case for admin dashboard to avoid it being active for all admin routes
    if (path === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/admin/';
    }
    
    // For any other path, check exact match or if it's a subpath
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };
  
  // Generate the style for active navigation items in header
  const getActiveStyle = (path: string) => {
    const active = isActive(path);
    return {
      mx: 1,
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '50%',
        width: '60%',
        transform: 'translateX(-50%)',
        borderBottom: active ? `3px solid ${theme.palette.secondary.main}` : 'none',
        borderRadius: '3px',
        opacity: active ? 1 : 0
      }
    };
  };
  
  // Generate style for mobile drawer active items
  const getActiveMobileStyle = (_path: string) => {
    return {
      '&.Mui-selected': {
        backgroundColor: `${theme.palette.primary.main}20`,
        borderLeft: `4px solid ${theme.palette.secondary.main}`,
      }
    };
  };
  
  // State for user menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  
  // State for mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Handle user menu open
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle user menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle drawer toggle
  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };

  // Menu ID
  const menuId = 'primary-account-menu';

  // Shared renderer for the mobile drawer's mapped student nav items.
  // Leaderboard keeps its historical delayed-navigate onClick (rather than a
  // plain Link) so the drawer visibly closes before the route changes —
  // preserved exactly here.
  const renderStudentMobileNavItem = (item: StudentNavItem) =>
    item.to === '/leaderboard' ? (
      <ListItem
        key={item.to}
        button
        selected={isActive(item.to)}
        sx={getActiveMobileStyle(item.to)}
        onClick={() => {
          toggleDrawer(false)({ type: 'click' } as React.MouseEvent);
          setTimeout(() => {
            navigate(item.to);
          }, 150); // Short delay to ensure drawer closes first
        }}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItem>
    ) : (
      <ListItem
        key={item.to}
        button
        component={Link}
        to={item.to}
        selected={isActive(item.to)}
        sx={getActiveMobileStyle(item.to)}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItem>
    );

  // Render drawer content
  const drawerContent = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Avatar 
          sx={{ 
            width: 64, 
            height: 64, 
            mb: 1, 
            bgcolor: theme.palette.primary.main 
          }}
        >
          {user?.firstName?.charAt(0) || 'U'}
        </Avatar>
        <Typography variant="h6" component="div">
          {user ? `${user.firstName} ${user.lastName}` : 'User'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.role === 'admin' || user?.role === 'super_admin' ? 'Administrator' :
           user?.role === 'parent' ? 'Parent' :
           user?.role === 'teacher' ? 'Teacher' : 'Student'}
        </Typography>
      </Box>
      <Divider />
      <List>
        {user?.role === 'admin' || user?.role === 'super_admin' ? (
          <>
            <ListItem 
              button 
              component={Link} 
              to="/admin"
              selected={isActive('/admin') && !isActive('/admin/')}
              sx={getActiveMobileStyle('/admin')}
            >
              <ListItemIcon>
                <AdminIcon />
              </ListItemIcon>
              <ListItemText primary="Admin Dashboard" />
            </ListItem>
            <ListItem 
              button 
              selected={isActive('/leaderboard')}
              sx={getActiveMobileStyle('/leaderboard')}
              onClick={() => {
                toggleDrawer(false)({ type: 'click' } as React.MouseEvent);
                setTimeout(() => {
                  navigate('/leaderboard');
                }, 150); // Short delay to ensure drawer closes first
              }}
            >
              <ListItemIcon>
                <EmojiEventsIcon />
              </ListItemIcon>
              <ListItemText primary="Leaderboard" />
            </ListItem>
            <ListItem 
              button 
              component={Link} 
              to="/admin/users"
              selected={isActive('/admin/users')}
              sx={getActiveMobileStyle('/admin/users')}
            >
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Manage Users" />
            </ListItem>
            <ListItem 
              button 
              component={Link} 
              to="/admin/sessions"
              selected={isActive('/admin/sessions')}
              sx={getActiveMobileStyle('/admin/sessions')}
            >
              <ListItemIcon>
                <SchoolIcon />
              </ListItemIcon>
              <ListItemText primary="Manage Sessions" />
            </ListItem>
            <ListItem 
              button 
              component={Link} 
              to="/admin/problems"
              selected={isActive('/admin/problems')}
              sx={getActiveMobileStyle('/admin/problems')}
            >
              <ListItemIcon>
                <CalculateIcon />
              </ListItemIcon>
              <ListItemText primary="Manage Problems" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/admin/payments"
              selected={isActive('/admin/payments')}
              sx={getActiveMobileStyle('/admin/payments')}
            >
              <ListItemIcon>
                <PaymentsIcon />
              </ListItemIcon>
              <ListItemText primary="Payments & Discounts" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/admin/quizzes"
              selected={isActive('/admin/quizzes')}
              sx={getActiveMobileStyle('/admin/quizzes')}
            >
              <ListItemIcon>
                <QuizIcon />
              </ListItemIcon>
              <ListItemText primary="Manage Quizzes" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/admin/teams"
              selected={isActive('/admin/teams')}
              sx={getActiveMobileStyle('/admin/teams')}
            >
              <ListItemIcon>
                <GroupsIcon />
              </ListItemIcon>
              <ListItemText primary="Manage Teams" />
            </ListItem>


          </>
        ) : user?.role === 'parent' ? (
          <>
            <ListItem
              button
              component={Link}
              to="/parent/dashboard"
              selected={isActive('/parent/dashboard')}
              sx={getActiveMobileStyle('/parent/dashboard')}
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
          </>
        ) : user?.role === 'teacher' ? (
          <>
            <ListItem
              button
              component={Link}
              to="/teacher/dashboard"
              selected={isActive('/teacher/dashboard')}
              sx={getActiveMobileStyle('/teacher/dashboard')}
            >
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/teacher/analytics"
              selected={isActive('/teacher/analytics')}
              sx={getActiveMobileStyle('/teacher/analytics')}
            >
              <ListItemIcon>
                <AssessmentIcon />
              </ListItemIcon>
              <ListItemText primary="Analytics" />
            </ListItem>
            <ListItem
              button
              component={Link}
              to="/teacher/ranked"
              selected={isActive('/teacher/ranked')}
              sx={getActiveMobileStyle('/teacher/ranked')}
            >
              <ListItemIcon>
                <MilitaryTechIcon />
              </ListItemIcon>
              <ListItemText primary="Class Ranking" />
            </ListItem>
          </>
        ) : (
          <>
            {studentNavItemsBeforeHintVideos.map(renderStudentMobileNavItem)}
            {/* Desktop-only omission (see studentNavItems.ts) — the mobile
                drawer keeps Hint Videos at its original position, since a
                vertical list has no width pressure. */}
            <ListItem
              button
              component={Link}
              to="/hint-videos"
              selected={isActive('/hint-videos')}
              sx={getActiveMobileStyle('/hint-videos')}
            >
              <ListItemIcon>
                <VideocamIcon />
              </ListItemIcon>
              <ListItemText primary="Hint Videos" />
            </ListItem>
            {studentNavItemsAfterHintVideos.map(renderStudentMobileNavItem)}
            <ListItem
              button
              component={Link}
              to="/messages"
              selected={isActive('/messages')}
              sx={getActiveMobileStyle('/messages')}
            >
              <ListItemIcon>
                <Badge badgeContent={unreadCount} color="error">
                  <MessageIcon />
                </Badge>
              </ListItemIcon>
              <ListItemText primary="Messages" />
            </ListItem>
          </>
        )}
        <Divider />
        <ListItem 
          button 
          component={Link} 
          to="/profile"
          selected={isActive('/profile')}
          sx={getActiveMobileStyle('/profile')}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="Profile Settings" />
        </ListItem>
        <ListItem button onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );

  // Render user menu
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      {/* Moved here from the top-level student nav — see studentNavItems.ts. */}
      {user?.role === 'student' && (
        <MenuItem
          component={Link}
          to="/hint-videos"
          onClick={handleMenuClose}
        >
          Hint Videos
        </MenuItem>
      )}
      <MenuItem
        component={Link}
        to="/profile"
        onClick={handleMenuClose}
      >
        Profile
      </MenuItem>
      <MenuItem onClick={handleLogout}>Logout</MenuItem>
    </Menu>
  );

  return (
    <>
      <AppBar position="static" color="primary" elevation={0}>
        <Container>
          <Toolbar disableGutters>
            {isMobile && isAuthenticated && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            {/* Logo and Title */}
            <Typography
              component={Link}
              to={isAuthenticated ? (
                user?.role === 'admin' || user?.role === 'super_admin' ? '/admin' :
                user?.role === 'parent' ? '/parent/dashboard' :
                user?.role === 'teacher' ? '/teacher/dashboard' :
                '/dashboard'
              ) : '/'}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  color: theme.palette.warning.main,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '50%',
                  p: 0.7,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <CalculateIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {tenantInfo?.presentsText && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      lineHeight: 1,
                      fontStyle: 'italic',
                      letterSpacing: 1,
                      opacity: 0.9,
                      fontWeight: 500,
                      mb: 0.2,
                      color: theme.palette.warning.main
                    }}
                  >
                    {tenantInfo.presentsText}
                  </Typography>
                )}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    fontWeight: 700,
                    lineHeight: 1
                  }}
                >
                  {tenantInfo?.siteName || 'MathWiz League'}
                </Typography>
              </Box>
            </Typography>
            
            <Box sx={{ flexGrow: 1 }} />
            
            {/* Desktop Navigation */}
            {!isMobile && isAuthenticated && (
              <Box sx={{ display: 'flex' }}>
                {user?.role === 'admin' || user?.role === 'super_admin' ? (
                  <>
                    <Button 
                      color="inherit" 
                      component={Link} 
                      to="/admin"
                      sx={getActiveStyle('/admin')}
                    >
                      Dashboard
                    </Button>
                    <Button 
                      color="inherit" 
                      onClick={() => navigate('/leaderboard')}
                      sx={getActiveStyle('/leaderboard')}
                    >
                      Leaderboard
                    </Button>
                    <Button 
                      color="inherit" 
                      component={Link} 
                      to="/admin/users"
                      sx={getActiveStyle('/admin/users')}
                    >
                      Users
                    </Button>
                    <Button 
                      color="inherit" 
                      component={Link} 
                      to="/admin/sessions"
                      sx={getActiveStyle('/admin/sessions')}
                    >
                      Sessions
                    </Button>
                    <Button
                      color="inherit"
                      component={Link}
                      to="/admin/payments"
                      sx={getActiveStyle('/admin/payments')}
                    >
                      Payments
                    </Button>
                    <Button
                      color="inherit"
                      component={Link}
                      to="/admin/quizzes"
                      sx={getActiveStyle('/admin/quizzes')}
                    >
                      Quizzes
                    </Button>
                    <Button
                      color="inherit"
                      component={Link}
                      to="/admin/teams"
                      sx={getActiveStyle('/admin/teams')}
                    >
                      Teams
                    </Button>

                    {/* Tenants link only for super_admin */}
                    {user?.role === 'super_admin' && (
                      <Button
                        color="inherit"
                        component={Link}
                        to="/admin/tenants"
                        sx={getActiveStyle('/admin/tenants')}
                      >
                        Tenants
                      </Button>
                    )}
                  </>
                ) : user?.role === 'parent' ? (
                  <>
                    <Button
                      color="inherit"
                      component={Link}
                      to="/parent/dashboard"
                      sx={getActiveStyle('/parent/dashboard')}
                    >
                      Dashboard
                    </Button>
                  </>
                ) : user?.role === 'teacher' ? (
                  <>
                    <Button
                      color="inherit"
                      component={Link}
                      to="/teacher/dashboard"
                      sx={getActiveStyle('/teacher/dashboard')}
                    >
                      Dashboard
                    </Button>
                    <Button
                      color="inherit"
                      component={Link}
                      to="/teacher/analytics"
                      sx={getActiveStyle('/teacher/analytics')}
                    >
                      Analytics
                    </Button>
                    <Button
                      color="inherit"
                      component={Link}
                      to="/teacher/ranked"
                      sx={getActiveStyle('/teacher/ranked')}
                    >
                      Class Ranking
                    </Button>
                  </>
                ) : (
                  <>
                    {studentNavItems.map(item => (
                      <Button
                        key={item.to}
                        color="inherit"
                        component={Link}
                        to={item.to}
                        sx={getActiveStyle(item.to)}
                      >
                        {item.label}
                      </Button>
                    ))}
                    <IconButton
                      color="inherit"
                      component={Link}
                      to="/messages"
                      sx={{
                        ...getActiveStyle('/messages'),
                        mx: 1
                      }}
                      title="Messages"
                    >
                      <Badge badgeContent={unreadCount} color="error">
                        <MessageIcon />
                      </Badge>
                    </IconButton>
                  </>
                )}
              </Box>
            )}
            
            {isAuthenticated ? (
              <IconButton
                edge="end"
                aria-label="account"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}>
                  {user?.firstName?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            ) : (
              <Box>
                <Button
                  color="inherit"
                  component={Link}
                  to="/login"
                  sx={{ mx: 1 }}
                >
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  component={Link}
                  to="/register"
                  sx={{ ml: 1 }}
                >
                  Register
                </Button>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      
      {renderMenu}
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}

export default Header;